import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiTable, FiBarChart2, FiUpload, FiEye } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const categories = ['pendidikan','kesehatan','ekonomi','infrastruktur','kependudukan','pertanian','pariwisata','sosial','lingkungan','pemerintahan'];
const chartTypes = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'horizontalBar', label: 'Horizontal Bar' },
  { value: 'line', label: 'Line Chart' },
  { value: 'doughnut', label: 'Donut Chart' },
  { value: 'none', label: 'Tanpa Grafik' }
];

const emptyForm = {
  judul: '', deskripsi: '', kategori: 'pendidikan', organisasi: '', tipeData: 'numerik',
  satuan: '', sumberData: '', tags: '', status: 'published', nilaiTerakhir: '',
  perubahanPersen: 0, tahunMulai: 2020, tahunAkhir: 2024,
  chartType: 'bar', chartXKey: '', chartYKey: ''
};

const DashboardDatasets = () => {
  const { user } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterKat, setFilterKat] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [activeTab, setActiveTab] = useState('info'); // info | columns | data | chart

  // Table builder
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [editRowIdx, setEditRowIdx] = useState(null);
  const [rowForm, setRowForm] = useState({});
  const [bulkText, setBulkText] = useState('');
  const [showBulk, setShowBulk] = useState(false);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15, sort: '-updatedAt' };
      if (search) params.search = search;
      if (filterKat) params.kategori = filterKat;

      // OPD: hanya lihat dataset sendiri, Admin: lihat semua
      const endpoint = isAdmin ? '/datasets' : '/datasets/my';
      const res = await api.get(endpoint, { params });
      setDatasets(res.data.data);
      setPagination(res.data.pagination);
    } catch (e) { toast.error('Gagal memuat dataset'); }
    finally { setLoading(false); }
  }, [search, filterKat, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // === DATASET CRUD ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [],
        nilaiTerakhir: Number(form.nilaiTerakhir) || 0,
        perubahanPersen: Number(form.perubahanPersen) || 0,
        tableColumns: columns,
        tableRows: rows.map(r => ({ data: r })),
        chartType: form.chartType,
        chartXKey: form.chartXKey,
        chartYKey: form.chartYKey,
      };
      if (!isAdmin) data.organisasi = user.organisasi || data.organisasi;

      if (editId) {
        await api.put(`/datasets/${editId}`, data);
        toast.success('Dataset berhasil diperbarui');
      } else {
        await api.post('/datasets', data);
        toast.success('Dataset berhasil dibuat');
      }
      closeModal(); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Gagal menyimpan'); }
  };

  const handleEdit = async (ds) => {
    setEditId(ds._id);
    setForm({
      judul: ds.judul, deskripsi: ds.deskripsi, kategori: ds.kategori,
      organisasi: ds.organisasi, tipeData: ds.tipeData, satuan: ds.satuan || '',
      sumberData: ds.sumberData || '', tags: ds.tags?.join(', ') || '',
      status: ds.status, nilaiTerakhir: ds.nilaiTerakhir || '',
      perubahanPersen: ds.perubahanPersen || 0, tahunMulai: ds.tahunMulai || 2020,
      tahunAkhir: ds.tahunAkhir || 2024, chartType: ds.chartType || 'bar',
      chartXKey: ds.chartXKey || '', chartYKey: ds.chartYKey || ''
    });
    setColumns(ds.tableColumns || []);
    setRows(ds.tableRows?.map(r => r.data || {}) || []);
    setActiveTab('info');
    setShowModal(true);
  };

  const handleDelete = async (id, judul) => {
    if (!window.confirm(`Hapus dataset "${judul}"?`)) return;
    try { await api.delete(`/datasets/${id}`); toast.success('Dataset dihapus'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Gagal menghapus'); }
  };

  const closeModal = () => {
    setShowModal(false); setEditId(null); setForm(emptyForm);
    setColumns([]); setRows([]); setActiveTab('info');
    setEditRowIdx(null); setRowForm({}); setShowBulk(false); setBulkText('');
  };

  // === COLUMN MANAGEMENT ===
  const addColumn = () => {
    const key = `col_${Date.now()}`;
    setColumns([...columns, { key, label: '', type: 'text' }]);
  };
  const updateColumn = (idx, field, value) => {
    const updated = [...columns];
    updated[idx][field] = value;
    setColumns(updated);
  };
  const removeColumn = (idx) => {
    const updated = columns.filter((_, i) => i !== idx);
    setColumns(updated);
    // Also clean rows
    const removedKey = columns[idx].key;
    setRows(rows.map(r => { const nr = { ...r }; delete nr[removedKey]; return nr; }));
  };

  // === ROW MANAGEMENT ===
  const addRow = () => {
    const emptyRow = {};
    columns.forEach(c => { emptyRow[c.key] = ''; });
    setEditRowIdx(rows.length);
    setRowForm(emptyRow);
  };
  const saveRow = () => {
    if (editRowIdx !== null) {
      const updated = [...rows];
      // Convert number fields
      const processed = { ...rowForm };
      columns.forEach(c => {
        if ((c.type === 'number' || c.type === 'year' || c.type === 'percent') && processed[c.key]) {
          processed[c.key] = Number(processed[c.key]) || 0;
        }
      });
      if (editRowIdx >= rows.length) updated.push(processed);
      else updated[editRowIdx] = processed;
      setRows(updated);
      setEditRowIdx(null);
      setRowForm({});
    }
  };
  const editRow = (idx) => {
    setEditRowIdx(idx);
    setRowForm({ ...rows[idx] });
  };
  const deleteRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
  };

  // === BULK IMPORT ===
  const handleBulkImport = () => {
    try {
      if (!bulkText.trim()) return toast.error('Teks kosong');
      const lines = bulkText.trim().split('\n');
      if (lines.length < 2) return toast.error('Minimal 2 baris (header + data)');

      const separator = lines[0].includes('\t') ? '\t' : ',';
      const headers = lines[0].split(separator).map(h => h.trim());

      // Auto-create columns if empty
      if (columns.length === 0) {
        const newCols = headers.map((h, i) => ({ key: `col_${i}`, label: h, type: 'text' }));
        setColumns(newCols);

        const newRows = lines.slice(1).filter(l => l.trim()).map(line => {
          const vals = line.split(separator).map(v => v.trim());
          const row = {};
          newCols.forEach((c, i) => { row[c.key] = vals[i] || ''; });
          return row;
        });
        setRows(newRows);
      } else {
        // Map by header label
        const newRows = lines.slice(1).filter(l => l.trim()).map(line => {
          const vals = line.split(separator).map(v => v.trim());
          const row = {};
          headers.forEach((h, i) => {
            const col = columns.find(c => c.label.toLowerCase() === h.toLowerCase());
            if (col) row[col.key] = vals[i] || '';
          });
          return row;
        });
        setRows([...rows, ...newRows]);
      }
      toast.success('Data berhasil diimport');
      setShowBulk(false);
      setBulkText('');
    } catch (e) { toast.error('Format data tidak valid'); }
  };

  const handleSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div>
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2>Kelola Dataset</h2>
          <p>{pagination.total} dataset {!isAdmin && '(milik Anda)'}</p>
        </div>
        <button className="btn btn-primary" onClick={() => { closeModal(); setShowModal(true); }}>
          <FiPlus /> Tambah Dataset
        </button>
      </div>

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', flex: 1, minWidth: 250 }}>
          <input className="form-input" placeholder="Cari dataset..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ borderRadius: '6px 0 0 6px' }} />
          <button type="submit" className="btn btn-primary" style={{ borderRadius: '0 6px 6px 0' }}><FiSearch /></button>
        </form>
        <select className="form-select" style={{ width: 180 }} value={filterKat} onChange={(e) => setFilterKat(e.target.value)}>
          <option value="">Semua Kategori</option>
          {categories.map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Judul</th><th>Kategori</th><th>Organisasi</th><th>Nilai</th><th>Tabel</th><th>Status</th><th>Aksi</th></tr></thead>
            <tbody>
              {datasets.map(ds => (
                <tr key={ds._id}>
                  <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{ds.judul}</td>
                  <td><span className={`card-kategori kat-${ds.kategori}`}>{ds.kategori}</span></td>
                  <td style={{ fontSize: '0.8rem', minWidth: 150 }}>{ds.organisasi}</td>
                  <td style={{ fontWeight: 600 }}>{ds.nilaiTerakhir?.toLocaleString('id-ID')} <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>{ds.satuan}</span></td>
                  <td><span className="badge badge-info">{ds.tableRows?.length || 0} baris</span></td>
                  <td><span className={`badge badge-${ds.status === 'published' ? 'success' : ds.status === 'draft' ? 'warning' : 'danger'}`}>{ds.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <Link to={`/dataset/${ds._id}`} className="btn btn-ghost btn-sm btn-icon" title="Lihat"><FiEye size={14} /></Link>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEdit(ds)} title="Edit"><FiEdit2 size={14} /></button>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDelete(ds._id, ds.judul)} title="Hapus" style={{ color: 'var(--danger)' }}><FiTrash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {datasets.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '2rem' }}>Belum ada dataset. Klik "Tambah Dataset" untuk mulai.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="pagination" style={{ marginTop: '1rem' }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={p === pagination.page ? 'active' : ''} onClick={() => load(p)}>{p}</button>
          ))}
        </div>
      )}

      {/* ======= MODAL ======= */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800, maxHeight: '95vh' }}>
            <div className="modal-header">
              <h3 style={{ fontFamily: 'var(--font-body)', fontSize: '1.1rem' }}>{editId ? 'Edit Dataset' : 'Tambah Dataset Baru'}</h3>
              <button className="btn btn-ghost btn-icon" onClick={closeModal}><FiX /></button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid var(--gray-100)', padding: '0 1.5rem' }}>
              {[
                { key: 'info', label: 'Info Dataset', icon: <FiEdit2 size={14} /> },
                { key: 'columns', label: 'Kolom Tabel', icon: <FiTable size={14} /> },
                { key: 'data', label: 'Isi Data', icon: <FiBarChart2 size={14} /> },
                { key: 'chart', label: 'Pengaturan Grafik', icon: <FiBarChart2 size={14} /> },
              ].map(tab => (
                <button key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '0.75rem 1rem', fontSize: '0.85rem', fontWeight: activeTab === tab.key ? 600 : 400,
                    color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                    borderBottom: activeTab === tab.key ? '2px solid var(--primary-500)' : '2px solid transparent',
                    background: 'none', border: 'none', borderBottomStyle: 'solid', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '-2px'
                  }}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ maxHeight: 'calc(95vh - 200px)', overflowY: 'auto' }}>

                {/* === TAB: INFO === */}
                {activeTab === 'info' && (
                  <>
                    <div className="form-group"><label className="form-label">Judul Dataset *</label><input className="form-input" value={form.judul} onChange={(e) => setForm({...form, judul: e.target.value})} required /></div>
                    <div className="form-group"><label className="form-label">Deskripsi *</label><textarea className="form-textarea" value={form.deskripsi} onChange={(e) => setForm({...form, deskripsi: e.target.value})} required /></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label className="form-label">Kategori *</label>
                        <select className="form-select" value={form.kategori} onChange={(e) => setForm({...form, kategori: e.target.value})}>
                          {categories.map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
                        </select>
                      </div>
                      <div className="form-group"><label className="form-label">Organisasi *</label><input className="form-input" value={form.organisasi} onChange={(e) => setForm({...form, organisasi: e.target.value})} required disabled={!isAdmin} /></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                      <div className="form-group"><label className="form-label">Satuan</label><input className="form-input" value={form.satuan} onChange={(e) => setForm({...form, satuan: e.target.value})} placeholder="Jiwa, %, Ton" /></div>
                      <div className="form-group"><label className="form-label">Nilai Terakhir</label><input className="form-input" type="number" value={form.nilaiTerakhir} onChange={(e) => setForm({...form, nilaiTerakhir: e.target.value})} /></div>
                      <div className="form-group"><label className="form-label">Perubahan %</label><input className="form-input" type="number" step="0.1" value={form.perubahanPersen} onChange={(e) => setForm({...form, perubahanPersen: e.target.value})} /></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group"><label className="form-label">Tahun Mulai</label><input className="form-input" type="number" value={form.tahunMulai} onChange={(e) => setForm({...form, tahunMulai: e.target.value})} /></div>
                      <div className="form-group"><label className="form-label">Tahun Akhir</label><input className="form-input" type="number" value={form.tahunAkhir} onChange={(e) => setForm({...form, tahunAkhir: e.target.value})} /></div>
                    </div>
                    <div className="form-group"><label className="form-label">Sumber Data</label><input className="form-input" value={form.sumberData} onChange={(e) => setForm({...form, sumberData: e.target.value})} /></div>
                    <div className="form-group"><label className="form-label">Tags (koma)</label><input className="form-input" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="stunting, balita, gizi" /></div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <select className="form-select" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                        <option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option>
                      </select>
                    </div>
                  </>
                )}

                {/* === TAB: COLUMNS === */}
                {activeTab === 'columns' && (
                  <>
                    <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--primary-50)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--primary-700)' }}>
                      Definisikan kolom tabel Anda. Kolom ini akan menjadi header tabel di halaman dataset.
                    </div>
                    {columns.map((col, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 40px', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                        <input className="form-input" placeholder="Nama kolom (misal: Kecamatan)" value={col.label}
                          onChange={(e) => updateColumn(idx, 'label', e.target.value)} style={{ padding: '0.5rem 0.75rem' }} />
                        <select className="form-select" value={col.type} onChange={(e) => updateColumn(idx, 'type', e.target.value)} style={{ padding: '0.5rem' }}>
                          <option value="text">Teks</option><option value="number">Angka</option><option value="year">Tahun</option><option value="percent">Persen</option>
                        </select>
                        <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeColumn(idx)} style={{ color: 'var(--danger)' }}><FiTrash2 size={14} /></button>
                      </div>
                    ))}
                    <button type="button" className="btn btn-secondary btn-sm" onClick={addColumn}><FiPlus size={14} /> Tambah Kolom</button>
                  </>
                )}

                {/* === TAB: DATA === */}
                {activeTab === 'data' && (
                  <>
                    {columns.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-400)' }}>
                        Silakan definisikan kolom dulu di tab "Kolom Tabel"
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                          <button type="button" className="btn btn-primary btn-sm" onClick={addRow}><FiPlus size={14} /> Tambah Baris</button>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowBulk(!showBulk)}><FiUpload size={14} /> Import CSV/Teks</button>
                          <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)', alignSelf: 'center' }}>{rows.length} baris data</span>
                        </div>

                        {/* Bulk import */}
                        {showBulk && (
                          <div style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 8 }}>
                            <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
                              Paste data dari Excel atau CSV. Baris pertama = header. Pisahkan dengan Tab atau Koma.
                            </div>
                            <textarea className="form-textarea" value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                              placeholder={`Contoh:\nKecamatan,Penduduk,Luas\nBajawa,38215,48.5\nGolewa,14320,98.6`}
                              style={{ minHeight: 100, fontFamily: 'monospace', fontSize: '0.8rem' }} />
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <button type="button" className="btn btn-primary btn-sm" onClick={handleBulkImport}>Import</button>
                              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowBulk(false); setBulkText(''); }}>Batal</button>
                            </div>
                          </div>
                        )}

                        {/* Data Table */}
                        <div style={{ overflowX: 'auto', border: '1px solid var(--gray-100)', borderRadius: 8 }}>
                          <table className="data-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                              <tr>
                                <th style={{ width: 40 }}>#</th>
                                {columns.map(c => <th key={c.key}>{c.label || c.key}</th>)}
                                <th style={{ width: 80 }}>Aksi</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, idx) => (
                                <tr key={idx}>
                                  <td>{idx + 1}</td>
                                  {columns.map(c => (
                                    <td key={c.key}>
                                      {editRowIdx === idx ? (
                                        <input className="form-input" style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem' }}
                                          type={c.type === 'number' || c.type === 'year' || c.type === 'percent' ? 'number' : 'text'}
                                          value={rowForm[c.key] || ''}
                                          onChange={(e) => setRowForm({ ...rowForm, [c.key]: e.target.value })} />
                                      ) : (
                                        c.type === 'number' ? Number(row[c.key] || 0).toLocaleString('id-ID') :
                                        c.type === 'percent' ? `${row[c.key] || 0}%` :
                                        row[c.key] || '-'
                                      )}
                                    </td>
                                  ))}
                                  <td>
                                    {editRowIdx === idx ? (
                                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <button type="button" className="btn btn-primary btn-sm" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={saveRow}>OK</button>
                                        <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => { setEditRowIdx(null); setRowForm({}); }}>X</button>
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => editRow(idx)} style={{ width: 28, height: 28 }}><FiEdit2 size={12} /></button>
                                        <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => deleteRow(idx)} style={{ width: 28, height: 28, color: 'var(--danger)' }}><FiTrash2 size={12} /></button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              {/* New row form */}
                              {editRowIdx !== null && editRowIdx >= rows.length && (
                                <tr style={{ background: 'var(--primary-50)' }}>
                                  <td>*</td>
                                  {columns.map(c => (
                                    <td key={c.key}>
                                      <input className="form-input" style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem' }}
                                        type={c.type === 'number' || c.type === 'year' || c.type === 'percent' ? 'number' : 'text'}
                                        value={rowForm[c.key] || ''}
                                        placeholder={c.label}
                                        onChange={(e) => setRowForm({ ...rowForm, [c.key]: e.target.value })} />
                                    </td>
                                  ))}
                                  <td>
                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                      <button type="button" className="btn btn-primary btn-sm" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={saveRow}>Simpan</button>
                                      <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }} onClick={() => { setEditRowIdx(null); setRowForm({}); }}>Batal</button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              {rows.length === 0 && editRowIdx === null && (
                                <tr><td colSpan={columns.length + 2} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '1.5rem' }}>Belum ada data. Klik "Tambah Baris" atau "Import CSV".</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* === TAB: CHART === */}
                {activeTab === 'chart' && (
                  <>
                    <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--gold-100)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--gold-600)' }}>
                      Pilih jenis grafik dan kolom yang digunakan untuk sumbu X dan Y. Grafik akan ditampilkan di halaman detail dataset.
                    </div>
                    <div className="form-group">
                      <label className="form-label">Jenis Grafik</label>
                      <select className="form-select" value={form.chartType} onChange={(e) => setForm({...form, chartType: e.target.value})}>
                        {chartTypes.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                      </select>
                    </div>
                    {form.chartType !== 'none' && columns.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div className="form-group">
                          <label className="form-label">Kolom Label (Sumbu X)</label>
                          <select className="form-select" value={form.chartXKey} onChange={(e) => setForm({...form, chartXKey: e.target.value})}>
                            <option value="">-- Pilih kolom --</option>
                            {columns.map(c => <option key={c.key} value={c.key}>{c.label || c.key}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Kolom Nilai (Sumbu Y)</label>
                          <select className="form-select" value={form.chartYKey} onChange={(e) => setForm({...form, chartYKey: e.target.value})}>
                            <option value="">-- Pilih kolom --</option>
                            {columns.filter(c => c.type === 'number' || c.type === 'percent' || c.type === 'year').map(c => <option key={c.key} value={c.key}>{c.label || c.key}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                    {columns.length === 0 && (
                      <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>Definisikan kolom tabel terlebih dahulu untuk mengatur grafik.</div>
                    )}
                  </>
                )}

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={closeModal}>Batal</button>
                <button type="submit" className="btn btn-primary">{editId ? 'Simpan Perubahan' : 'Buat Dataset'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDatasets;
